import {
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import {KeyboardDatePicker} from '@material-ui/pickers';
import States from 'ApplicationEntry/models/States';
import React, {useState} from 'react';
import {useDispatch} from 'react-redux';

import {EmptyPhoneNumber, isValidPhoneNumber} from 'Core/models/PhoneNumber';
import {ValidPhoneNumber} from 'Core/models/PhoneNumber';
import {maskPhoneNumber, unmaskPhoneNumber} from 'Shared/utilities/phoneNumberMasker';

import {
  changeAddress,
  changeAddressLine2,
  changeApplicantIsOwner,
  changeCity,
  changeDateOfBirth,
  changeIsMailingAddress,
  changeOwnerName,
  changeOwnerSigned,
  changePhone,
  changeSameAddressAsApplicant,
  changeSocialSecurityNumber,
  changeState,
  changeZip,
} from 'ApplicationEntry/ducks/ownerInfo';
import {ApplicantDemographics} from 'ApplicationEntry/models/ApplicantDemographics';
import {OwnerInfo} from 'ApplicationEntry/models/OwnerInfo';
import {isValidSocialSecurityNumber} from 'ApplicationEntry/models/SocialSecurityNumber';
import {EmptyZipCode, isValidZipCode, maskZipCode, unmaskZipCode, ValidZipCode} from 'ApplicationEntry/models/ZipCode';

import {maskTaxId, unMaskTaxId} from 'Shared/utilities/taxIdMasker';
import * as BAActions from 'ApplicationEntry/ducks/billingAgent';
import './OwnerInfo.sass';
import secondaryAddressee from 'ApplicationEntry/ducks/secondaryAddressee';

const REQUIRED_DOB_MESSAGE = 'Please enter a valid date.';
const INVALID_SSN_FORMAT = 'Please enter a complete social security number.';
const INVALID_PHONE_FORMAT = 'Please enter a complete phone number.';
const INVALID_ZIP_FORMAT = 'Please enter a complete zip code.';

type OwnerInfoProps = {
  ownerInfo: OwnerInfo;
  applicantDemographics: ApplicantDemographics;
};

const isInvalidDate = (date: Date) => isNaN(date.getTime());
const getSsnErrorMessage = () => INVALID_SSN_FORMAT;
const getPhoneErrorMessage = () => INVALID_PHONE_FORMAT;
const getZipErrorMessage = () => INVALID_ZIP_FORMAT;

const OwnerInfoSection = (props: OwnerInfoProps) => {
  const {ownerInfo, applicantDemographics} = props;
  const dispatch = useDispatch();

  const [stateLabel, setStateLabel] = useState<HTMLLabelElement | null>();

  const [hasSsnBeenBlurred, setHasSsnBeenBlurred] = useState(false);
  const [hasDateOfBirthBeenBlurred, setHasDateOfBirthBeenBlurred] = useState(false);
  const [hasPhoneBeenBlurred, setHasPhoneBeenBlurred] = useState(false);
  const [hasZipBeenBlurred, setHasZipBeenBlurred] = useState(false);

  const showSSNError =
    hasSsnBeenBlurred &&
    ownerInfo.socialSecurityNumber.value !== '' &&
    !isValidSocialSecurityNumber(ownerInfo.socialSecurityNumber);
  const showDateOfBirthError =
    hasDateOfBirthBeenBlurred && ownerInfo.dateOfBirth !== '' && isInvalidDate(ownerInfo.dateOfBirth);
  const showPhoneError =
    hasPhoneBeenBlurred && ownerInfo.phone.phoneNumber !== '' && !isValidPhoneNumber(ownerInfo.phone);
  const showZipError = hasZipBeenBlurred && ownerInfo.zip.value !== '' && !isValidZipCode(ownerInfo.zip);

  const checkedApplicant = (e: any) => {
    if (e) {
      dispatch(changeSameAddressAsApplicant(true));
      dispatch(changeAddress(applicantDemographics.address.value));
      dispatch(changeAddressLine2(applicantDemographics.addressLine2.value));
      dispatch(changeCity(applicantDemographics.city.value));
      dispatch(changeState(applicantDemographics.state.value));
      dispatch(changeZip(applicantDemographics.zip));
    } else {
      dispatch(changeSameAddressAsApplicant(false));
      dispatch(changeAddress(''));
      dispatch(changeAddressLine2(''));
      dispatch(changeCity(''));
      dispatch(changeState(''));
      dispatch(changeZip(EmptyZipCode));
    }
  };

  const checkedApplicantInfo = (isApplicantOwner: boolean) => {
    if (isApplicantOwner) {
      dispatch(changeApplicantIsOwner(isApplicantOwner));
      dispatch(changeSameAddressAsApplicant(isApplicantOwner));
      dispatch(changeOwnerName(applicantDemographics.name.value));
      dispatch(changeSocialSecurityNumber(unMaskTaxId(applicantDemographics.socialSecurityNumber.value)));
      dispatch(changePhone(ValidPhoneNumber.create(unmaskPhoneNumber(applicantDemographics.phone.phoneNumber))));
      dispatch(changeDateOfBirth(applicantDemographics.dateOfBirth));
      dispatch(changeAddress(applicantDemographics.address.value));
      dispatch(changeAddressLine2(applicantDemographics.addressLine2.value));
      dispatch(changeCity(applicantDemographics.city.value));
      dispatch(changeState(applicantDemographics.state.value));
      dispatch(changeZip(applicantDemographics.zip));
    } else {
      dispatch(changeApplicantIsOwner(isApplicantOwner));
      dispatch(changeSameAddressAsApplicant(isApplicantOwner));
      dispatch(changeOwnerName(''));
      dispatch(changeSocialSecurityNumber(''));
      dispatch(changePhone(new EmptyPhoneNumber()));
      dispatch(changeDateOfBirth(''));
      dispatch(changeAddress(''));
      dispatch(changeAddressLine2(''));
      dispatch(changeCity(''));
      dispatch(changeState(''));
      dispatch(changeZip(EmptyZipCode));
      dispatch(changeIsMailingAddress(false));
      dispatch(changeOwnerSigned(false));
    }
  };

  return (
    <form noValidate autoComplete="off" className="owner-info-section">
      <div className="owner-info-section__title-container">
        <Typography variant="subtitle1" className="owner-info-section__title-container-title">
          Owner Information
        </Typography>
      </div>

      <div className="owner-info-section__row">
        <Grid container spacing={4}>
          <Grid item xs={12} md={3}>
            <FormControlLabel
              className="owner-info-section__text-field"
              control={
                <Checkbox
                  checked={ownerInfo.applicantIsOwner}
                  onChange={e => checkedApplicantInfo(e.target.checked)}
                  name="checkedB"
                  color="secondary"
                />
              }
              label={<Typography>Applicant Is Owner</Typography>}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              id="owner-name"
              label="Owner"
              variant="outlined"
              placeholder="Enter Owner"
              margin="normal"
              fullWidth
              className="owner-info-section__text-field"
              value={ownerInfo.ownerName.value}
              onChange={e => {
                dispatch(changeOwnerName(e.target.value));
                dispatch(BAActions.getAchPayerName(e.target.value));
                dispatch(BAActions.changePayor(e.target.value));
              }}
              disabled={ownerInfo.applicantIsOwner}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              id="owner-ssn"
              label="Owner SSN"
              error={showSSNError}
              helperText={showSSNError ? getSsnErrorMessage() : ''}
              variant="outlined"
              placeholder="Owner SSN"
              margin="normal"
              fullWidth
              onBlur={() => setHasSsnBeenBlurred(true)}
              autoComplete="off"
              inputProps={{
                maxLength: 11,
              }}
              value={maskTaxId(ownerInfo.socialSecurityNumber.value)}
              onChange={e => dispatch(changeSocialSecurityNumber(unMaskTaxId(e.target.value)))}
              className="owner-info-section__text-field"
              disabled={ownerInfo.applicantIsOwner}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <KeyboardDatePicker
              inputVariant="outlined"
              error={showDateOfBirthError}
              helperText={showDateOfBirthError ? REQUIRED_DOB_MESSAGE : ''}
              label="Date of Birth"
              format="MM/dd/yyyy"
              variant="inline"
              className="owner-info-section__text-field"
              onBlur={() => setHasDateOfBirthBeenBlurred(true)}
              value={ownerInfo.dateOfBirth || null}
              autoOk={true}
              onChange={date => dispatch(changeDateOfBirth(date?.toJSDate() || ''))}
              disabled={ownerInfo.applicantIsOwner}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              id="owner-info-phone-number"
              label="Phone Number"
              variant="outlined"
              placeholder="(XXX)-XXX XXXX"
              error={showPhoneError}
              onBlur={() => setHasPhoneBeenBlurred(true)}
              helperText={showPhoneError ? getPhoneErrorMessage() : ''}
              margin="normal"
              fullWidth
              inputProps={{
                maxLength: 14,
              }}
              value={maskPhoneNumber(ownerInfo.phone.phoneNumber)}
              onChange={e => dispatch(changePhone(ValidPhoneNumber.create(unmaskPhoneNumber(e.target.value))))}
              className="owner-info-section__text-field"
              disabled={ownerInfo.applicantIsOwner}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControlLabel
              className="owner-info-section__text-field"
              control={
                <Checkbox
                  checked={ownerInfo.sameAddressAsApplicant}
                  onChange={e => checkedApplicant(e.target.checked)}
                  name="checkedB"
                  color="secondary"
                  disabled={ownerInfo.applicantIsOwner}
                />
              }
              label={<Typography>Same Address as Applicant</Typography>}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              id="owner-address"
              label="Address"
              variant="outlined"
              placeholder="Enter Address"
              margin="normal"
              className="owner-info-section__text-field"
              value={ownerInfo.address.value}
              onChange={e => dispatch(changeAddress(e.target.value))}
              disabled={ownerInfo.applicantIsOwner || ownerInfo.sameAddressAsApplicant}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              id="owner-apt"
              label="Address Line 2"
              variant="outlined"
              placeholder="Apartment, Suite, Building, Floor, etc..."
              margin="normal"
              className="owner-info-section__text-field"
              value={ownerInfo.addressLine2.value}
              onChange={e => dispatch(changeAddressLine2(e.target.value))}
              disabled={ownerInfo.applicantIsOwner || ownerInfo.sameAddressAsApplicant}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              id="owner-city"
              label="City"
              variant="outlined"
              placeholder="Enter City"
              margin="normal"
              className="owner-info-section__text-field"
              value={ownerInfo.city.value}
              onChange={e => dispatch(changeCity(e.target.value))}
              disabled={ownerInfo.applicantIsOwner || ownerInfo.sameAddressAsApplicant}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl variant="outlined" className="owner-info-section__text-field">
              <InputLabel ref={setStateLabel} id="owner-state-label">
                State
              </InputLabel>
              <Select
                labelWidth={stateLabel?.clientWidth}
                labelId="owner-state-label"
                id="select-owner-state"
                value={ownerInfo.state.value}
                onChange={e => dispatch(changeState(e.target.value as string))}
                disabled={ownerInfo.applicantIsOwner || ownerInfo.sameAddressAsApplicant}
              >
                {States.map(stateCode => (
                  <MenuItem value={stateCode} key={stateCode}>
                    {stateCode}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              className="owner-info-section__text-field"
              error={showZipError}
              helperText={showZipError ? getZipErrorMessage() : ''}
              id="owner-zip"
              inputProps={{maxLength: 10}}
              label="Zip"
              onBlur={() => setHasZipBeenBlurred(true)}
              onChange={e => dispatch(changeZip(ValidZipCode.create(unmaskZipCode(e.target.value))))}
              placeholder="XXXXX-XXXX"
              value={maskZipCode(ownerInfo.zip.value)}
              variant="outlined"
              disabled={ownerInfo.applicantIsOwner || ownerInfo.sameAddressAsApplicant}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControlLabel
              className="owner-info-section__text-field"
              control={
                <Checkbox
                  checked={ownerInfo.ownerSigned}
                  onChange={e => dispatch(changeOwnerSigned(e.target.checked))}
                  name="checkedB"
                  color="secondary"
                />
              }
              label={<Typography>Owner Signed</Typography>}
            />
          </Grid>

          {/* "Use as mailing address" features is not required at present */}
          {/* <Grid item xs={12} md={3}>
            <FormControlLabel
              className="owner-info-section__text-field"
              control={
                <Checkbox
                  checked={ownerInfo.isMailingAddress}
                  onChange={e => dispatch(changeIsMailingAddress(e.target.checked))}
                  name="checkedB"
                  color="secondary"
                />
              }
              label={<Typography>Use as Mailing Address</Typography>}
            />
          </Grid> */}
        </Grid>
      </div>
      <Divider className="application-form__actions-divider" />
    </form>
  );
};

export default OwnerInfoSection;
